import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import { toRecordBy } from '#shared';

import {
  useQueryActiveCases,
  useQueryAircraftStatuses,
  useQueryHubsResourceReport,
  useQueryMetars,
  useQueryNewCases,
  useQuerySimpleStatistics,
  useQuerySurgeons,
  useQueryTafs,
} from '../../api';
import {
  getMatchDetails,
  type SearchableCase,
  toSearchableCase,
} from '../../helpers';

import type { ActiveCase, AircraftStatus, AirMetar, NewCase } from '#entities';
import type { AppDataErrors } from '#shared';

export type DataContextProps = {
  rawData: RawData;
  backendNetworkError: string;
  appDataErrors: AppDataErrors;
  lastUpdateTime: Date | null;
  lastSentTime: Date | null;
  lastReceiveTime?: Date | null;
  aircraftsRawList: AircraftStatus[];
  tafsRawData: Record<string, string>;
  metarsData: Record<string, AirMetar>;
  searchText: string;
  setSearchText: (value: string) => void;
  filteredActiveCases: ActiveCase[];
  activeCases: ActiveCase[];
  filteredNewCases: NewCase[];
  newCases: NewCase[];
  dataErrors: AppDataErrors;
};

export type RawData = {
  dataErrors: AppDataErrors;
  networkErrors: AppDataErrors;
};

const defaultRawData = {
  allData: {
    dataErrors: {},
    networkErrors: {},
  },
  backendNetworkError: '',
};
export const DataContext = createContext<DataContextProps | undefined>(
  undefined,
);

export const useData = (): DataContextProps => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};

export const DataProvider = ({ children }: PropsWithChildren) => {
  const { data: metarsRawData } = useQueryMetars();
  const { data: tafsRawData } = useQueryTafs();

  const [searchText, setSearchText] = useState('');

  const metarsArray = useMemo(() => metarsRawData ?? [], [metarsRawData]);

  const metarsData = useMemo(
    () => toRecordBy(metarsArray, 'stationId'),
    [metarsArray],
  );

  const {
    data: newCases,
    validation: newCasesValidation,
    error: newCasesError,
  } = useQueryNewCases();
  const {
    validation: aircraftStatusesValidation,
    error: aircraftStatusesError,
  } = useQueryAircraftStatuses();
  const {
    validation: simpleStatisticsValidation,
    error: simpleStatisticsError,
  } = useQuerySimpleStatistics();
  const {
    validation: hubsResourceReportValidation,
    error: hubsResourceReportError,
  } = useQueryHubsResourceReport();
  const { validation: surgeonsValidation, error: surgeonsError } =
    useQuerySurgeons();
  const {
    data: activeCases,
    validation: activeCasesValidation,
    error: activeCasesError,
    lastSentTime: activeCasesLastSentTime,
    lastReceiveTime: activeCasesLastReceiveTime,
  } = useQueryActiveCases();
  const { data: rawAircraftStatuses } = useQueryAircraftStatuses();

  const filterBySearch = useCallback(
    // eslint-disable-next-line comma-spacing
    <T,>(list: T[], toSearchable: (x: T) => SearchableCase) => {
      const q = searchText.trim();
      if (!q) {
        return list;
      }
      return list.filter(
        item => getMatchDetails(toSearchable(item), q).matched,
      );
    },
    [searchText],
  );

  const filteredActiveCases = useMemo(
    () => filterBySearch<ActiveCase>(activeCases, toSearchableCase),
    [filterBySearch, activeCases],
  );

  const filteredNewCases = useMemo(
    () => filterBySearch<NewCase>(newCases, toSearchableCase),
    [filterBySearch, newCases],
  );

  const dataErrors: AppDataErrors = useMemo(() => {
    const errors: AppDataErrors = {
      activeCases: {},
      newCases: {},
    };

    if (activeCasesValidation && !activeCasesValidation.isValid) {
      activeCasesValidation.issues.forEach(issue => {
        if (!errors.activeCases[issue.key]) {
          errors.activeCases[issue.key] = {
            label: issue.key,
            errorMessages: [],
          };
        }
        errors.activeCases[issue.key].errorMessages.push(issue.message);
      });
    }

    if (newCasesValidation && !newCasesValidation.isValid) {
      newCasesValidation.issues.forEach(issue => {
        if (!errors.newCases[issue.key]) {
          errors.newCases[issue.key] = {
            label: issue.key,
            errorMessages: [],
          };
        }
        errors.newCases[issue.key].errorMessages.push(issue.message);
      });
    }
    if (aircraftStatusesValidation && !aircraftStatusesValidation.isValid) {
      aircraftStatusesValidation.issues.forEach(issue => {
        if (!errors.aircraftStatuses) {
          errors.aircraftStatuses = {};
        }
        if (!errors.aircraftStatuses[issue.key]) {
          errors.aircraftStatuses[issue.key] = {
            label: issue.key,
            errorMessages: [],
          };
        }
        errors.aircraftStatuses[issue.key].errorMessages.push(issue.message);
      });
    }
    if (hubsResourceReportValidation && !hubsResourceReportValidation.isValid) {
      hubsResourceReportValidation.issues.forEach(issue => {
        if (!errors.hubsResourceReport) {
          errors.hubsResourceReport = {};
        }
        if (!errors.hubsResourceReport[issue.key]) {
          errors.hubsResourceReport[issue.key] = {
            label: issue.key,
            errorMessages: [],
          };
        }
        errors.hubsResourceReport[issue.key].errorMessages.push(issue.message);
      });
    }
    if (surgeonsValidation && !surgeonsValidation.isValid) {
      surgeonsValidation.issues.forEach(issue => {
        if (!errors.surgeons) {
          errors.surgeons = {};
        }
        if (!errors.surgeons[issue.key]) {
          errors.surgeons[issue.key] = {
            label: issue.key,
            errorMessages: [],
          };
        }
        errors.surgeons[issue.key].errorMessages.push(issue.message);
      });
    }
    if (simpleStatisticsValidation && !simpleStatisticsValidation.isValid) {
      simpleStatisticsValidation.issues.forEach(issue => {
        if (!errors.simpleStatistics) {
          errors.simpleStatistics = {};
        }
        if (!errors.simpleStatistics[issue.key]) {
          errors.simpleStatistics[issue.key] = {
            label: issue.key,
            errorMessages: [],
          };
        }
        errors.simpleStatistics[issue.key].errorMessages.push(issue.message);
      });
    }

    return errors;
  }, [
    activeCasesValidation,
    newCasesValidation,
    aircraftStatusesValidation,
    hubsResourceReportValidation,
    surgeonsValidation,
    simpleStatisticsValidation,
  ]);

  const providerValue = useMemo(
    () => ({
      rawData: defaultRawData.allData,
      backendNetworkError: defaultRawData.backendNetworkError,
      lastUpdateTime: activeCasesLastReceiveTime,
      // SpecRef: ARCH-005
      // Reference: docs/decisions/ARCH-005-ui-error-detection-resilience.md#4-appdataerrors-aggregation
      // Aggregates errors from all 6 query hooks into a single AppDataErrors object for widget display.
      appDataErrors: ((): AppDataErrors => {
        const errorMessages = [
          activeCasesError,
          newCasesError,
          aircraftStatusesError,
          simpleStatisticsError,
          hubsResourceReportError,
          surgeonsError,
        ]
          .filter(
            (e): e is NonNullable<typeof e> => e !== null && e !== undefined,
          )
          .map(e =>
            e instanceof Error ? e.message : 'Unknown error occurred',
          );
        if (errorMessages.length === 0) {
          return {};
        }
        return { global: { query: { label: 'Query Error', errorMessages } } };
      })(),
      lastSentTime: activeCasesLastSentTime,
      lastReceiveTime: activeCasesLastReceiveTime,
      aircraftsRawList: rawAircraftStatuses,
      metarsData,
      tafsRawData,
      dataErrors,
      searchText,
      setSearchText,
      filteredActiveCases,
      filteredNewCases,
      newCases,
      activeCases,
    }),
    [
      activeCasesLastReceiveTime,
      activeCasesError,
      newCasesError,
      aircraftStatusesError,
      simpleStatisticsError,
      hubsResourceReportError,
      surgeonsError,
      activeCasesLastSentTime,
      rawAircraftStatuses,
      metarsData,
      tafsRawData,
      dataErrors,
      searchText,
      filteredActiveCases,
      filteredNewCases,
      newCases,
      activeCases,
    ],
  );

  return (
    <DataContext.Provider value={providerValue}>
      {children}
    </DataContext.Provider>
  );
};
